// src/components/StatsSlider.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { ReactComponent as rightArrowIcon } from "../assets/carousel-icons/arrow-right.svg";

const PURPLE = "#9C4FFF";
const STAT_CARD_BORDER = "#9C4FFF";
const STAT_CARD_ACTIVE_BG = "#F9F5FF";
const STAT_CARD_DEFAULT_BG = "#FFFFFF";
const STAT_CARD_DISABLED_BORDER = "#D2D8DE";
const STAT_CARD_DISABLED_TEXT = "#98A2B3";
const STAT_CARD_TEXT = "#54595F";
const STAT_CARD_RADIUS = "8px";

function parseMetricValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const normalized = String(value ?? "")
    .trim()
    .replace(/,/g, "");

  if (!normalized || !/^-?\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }

  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export default function StatsSlider({
  items = [],
  autoPlay = true,
  interval = 2500,
  pauseOnHover = true,
  loop = true,
  showArrows = true,
  cardMinWidth = 200,
  gap = 24,
  onItemClick,
  activeItemId = null,
}) {
  const scrollerRef = useRef(null);
  const [isHover, setIsHover] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuItemId, setMenuItemId] = useState(null);

  // baseline = "default slide" scrollLeft (don’t assume 0)
  const baselineLeftRef = useRef(null);

  const [scrollMeta, setScrollMeta] = useState({
    left: 0,
    maxLeft: 0,
    hasOverflow: false,
  });

  // Keep the design's fixed card width; reveal overflow by scrolling.
  const slideWidth = cardMinWidth;

  const hasItems = useMemo(() => items?.length > 0, [items]);
  const activeMenuItem = useMemo(
    () =>
      Array.isArray(items)
        ? items.find((item) => String(item?.id ?? "") === String(menuItemId ?? ""))
        : null,
    [items, menuItemId]
  );
  const activeMenuOptions = Array.isArray(activeMenuItem?.menuOptions)
    ? activeMenuItem.menuOptions
    : [];

  const EPS = 2;

  const getStep = useCallback(() => {
    // ✅ step is exactly slideWidth + gap (deterministic; no DOM measuring needed)
    return slideWidth + gap;
  }, [slideWidth, gap]);

  const readScrollMeta = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const left = el.scrollLeft;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const hasOverflow = maxLeft > EPS;

    // capture baseline only once (the real "default slide" position)
    if (baselineLeftRef.current === null) {
      baselineLeftRef.current = left;
    }

    setScrollMeta({ left, maxLeft, hasOverflow });
  }, []);

  const scrollByStep = useCallback(
    (dir = 1) => {
      const el = scrollerRef.current;
      if (!el) return;

      const step = getStep();
      if (!step) return;

      const baseline = baselineLeftRef.current ?? 0;
      const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);

      const leftNow = el.scrollLeft;
      const nextLeft = leftNow + dir * step;

      if (loop) {
        // ---- RIGHT: if we're not yet at the true end, go to maxLeft first
        if (dir > 0 && nextLeft >= maxLeft - EPS) {
          if (leftNow < maxLeft - EPS) {
            el.scrollTo({ left: maxLeft, behavior: "smooth" });
            return;
          }
          el.scrollTo({ left: baseline, behavior: "smooth" });
          return;
        }

        // ---- LEFT: if we're not yet back to baseline, go to baseline first
        if (dir < 0 && nextLeft <= baseline + EPS) {
          if (leftNow > baseline + EPS) {
            el.scrollTo({ left: baseline, behavior: "smooth" });
            return;
          }
          el.scrollTo({ left: maxLeft, behavior: "smooth" });
          return;
        }
      } else {
        // clamp in non-loop mode
        if (nextLeft >= maxLeft - EPS) {
          el.scrollTo({ left: maxLeft, behavior: "smooth" });
          return;
        }
        if (nextLeft <= baseline + EPS) {
          el.scrollTo({ left: baseline, behavior: "smooth" });
          return;
        }
      }

      el.scrollBy({ left: dir * step, behavior: "smooth" });
    },
    [getStep, loop]
  );
  // ✅ When slide width changes, re-read meta (maxLeft depends on layout)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // allow layout to settle before reading
    const raf = requestAnimationFrame(() => readScrollMeta());
    return () => cancelAnimationFrame(raf);
  }, [slideWidth, items?.length, readScrollMeta]);

  // Keep meta updated (scroll + scroller resize)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    readScrollMeta();

    const onScroll = () => readScrollMeta();
    el.addEventListener("scroll", onScroll, { passive: true });

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => readScrollMeta());
      ro.observe(el);
    } else {
      window.addEventListener("resize", readScrollMeta);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", readScrollMeta);
    };
  }, [items?.length, readScrollMeta]);

  // autoplay: moves right
  useEffect(() => {
    if (!autoPlay) return;
    if (!items?.length) return;
    if (pauseOnHover && isHover) return;

    const el = scrollerRef.current;
    if (!el) return;

    const t = setInterval(() => scrollByStep(1), interval);
    return () => clearInterval(t);
  }, [autoPlay, interval, pauseOnHover, isHover, items?.length, loop, scrollByStep]);

  const baseline = baselineLeftRef.current ?? 0;
  const closeItemMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuItemId(null);
  }, []);

  // ✅ left arrow only after we have moved right from baseline (default)
  const showLeftArrow =
    showArrows &&
    hasItems &&
    scrollMeta.hasOverflow &&
    scrollMeta.left > baseline + EPS;

  // ✅ determine if we're at the end (only meaningful in non-loop mode)
  const atEnd = scrollMeta.left >= scrollMeta.maxLeft - EPS;

  // ✅ right arrow hides at the end when loop is false
  const showRightArrow =
    showArrows &&
    hasItems &&
    scrollMeta.hasOverflow &&
    (loop ? true : !atEnd);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderBottom: `1px solid #D2D8DE`,
        paddingLeft: "32px",
      }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {/* Scroll container */}
      <Box
        ref={scrollerRef}
        sx={{
          display: "flex",
          alignItems: "stretch",
          marginLeft: 0,
          marginRight: "80px",
          gap: `${gap}px`,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          py: "24px",
          "&::-webkit-scrollbar": { height: 0 },
          scrollbarWidth: "none",
        }}
      >
        {items.map((it, idx) => {
          const isLast = idx === items.length - 1;
          const metricValue = parseMetricValue(it?.value);
          const hasClickHandler =
            typeof onItemClick === "function" || typeof it?.onClick === "function";
          // A zero count is still a useful filter: it should show the empty
          // result set rather than making the metric impossible to select.
          const isDisabled = Boolean(it.disabled) || (metricValue === 0 && !hasClickHandler);
          const disabledColor = it.disabledColor || STAT_CARD_DISABLED_TEXT;
          const disabledBorder = it.disabledBorder || STAT_CARD_DISABLED_BORDER;
          const activeBackground = it.activeBackground || STAT_CARD_ACTIVE_BG;
          const isActive =
            !isDisabled &&
            activeItemId !== null &&
            activeItemId !== undefined &&
            String(activeItemId) === String(it.id);
          const isClickable = !isDisabled && hasClickHandler;
          const hasMenuOptions = Array.isArray(it?.menuOptions) && it.menuOptions.length > 0;

          const handleItemClick = () => {
            if (typeof it?.onClick === "function") {
              it.onClick(it, idx);
              return;
            }
            if (typeof onItemClick === "function") {
              onItemClick(it, idx);
            }
          };

          const handleMenuOpen = (event) => {
            event.preventDefault();
            event.stopPropagation();
            setMenuAnchorEl(event.currentTarget);
            setMenuItemId(it.id ?? `${it.label}-${idx}`);
          };

          return (
            <Box
              key={it.id ?? `${it.label}-${idx}`}
              role={hasClickHandler ? "button" : undefined}
              aria-disabled={isDisabled || undefined}
              aria-pressed={isClickable ? isActive : undefined}
              tabIndex={isClickable ? 0 : undefined}
              title={String(it?.description || it?.title || "").trim() || undefined}
              aria-label={
                String(it?.description || "").trim()
                  ? `${it.label}: ${it.value}. ${it.description}`
                  : undefined
              }
              onClick={isClickable ? handleItemClick : undefined}
              onKeyDown={
                isClickable
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleItemClick();
                      }
                    }
                  : undefined
              }
              sx={{
                flex: "0 0 auto",
                // Preserve card dimensions instead of stretching to fill the row.
                width: `${slideWidth}px`,
                minWidth: `${slideWidth}px`,
                height: "72px",
                minHeight: "72px",
                scrollSnapAlign: isLast ? "end" : "start",
                border: isDisabled
                  ? `1px solid ${disabledBorder}`
                  : `1px solid ${it.color || STAT_CARD_BORDER}`,
                borderRadius: STAT_CARD_RADIUS,
                backgroundColor: isActive ? activeBackground : STAT_CARD_DEFAULT_BG,
                px: "24px",
                py: 0,
                lineHeight: 1.1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "16px",
                cursor: isClickable ? "pointer" : "default",
                transition: "background-color 0.2s ease, border-color 0.2s ease",
                outline: "none",
                "&:hover, &:focus-visible": isClickable
                  ? {
                      backgroundColor: activeBackground,
                    }
                  : undefined,
              }}
              >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 24,
                  lineHeight: 1,
                  textBoxTrim: "trim-both",
                  textBoxEdge: "cap alphabetic",
                  fontFamily: "Be Vietnam Pro",
                  color: isDisabled ? disabledColor : it.color || STAT_CARD_TEXT,
                }}
              >
                {it.value}
              </Typography>

              <Box
                sx={{
                  mt: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "20px",
                    textBoxTrim: "trim-both",
                    textBoxEdge: "cap alphabetic",
                    color: isDisabled ? disabledColor : it.color || STAT_CARD_TEXT,
                    fontFamily: "Be Vietnam Pro",
                    opacity: it.color ? 1 : 0.95,
                    minWidth: 0,
                  }}
                >
                  {it.label}
                </Typography>

                {hasMenuOptions ? (
                  <Box
                    component="button"
                    type="button"
                    disabled={isDisabled}
                    onClick={handleMenuOpen}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      px: 0.75,
                      py: 0.35,
                      border: `1px solid ${
                        isDisabled ? disabledBorder : "rgba(156,79,255,0.32)"
                      }`,
                      borderRadius: "999px",
                      backgroundColor: "#FFFFFF",
                      color: isDisabled ? disabledColor : PURPLE,
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: "Be Vietnam Pro",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    <Box component="span">{it.selectedOptionLabel || "Select"}</Box>
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14 }} />
                  </Box>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl && activeMenuItem)}
        onClose={closeItemMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
          },
        }}
      >
        {activeMenuOptions.map((option, index) => {
          const normalizedOption =
            option && typeof option === "object"
              ? option
              : { value: option, label: String(option ?? "") };

          return (
            <MenuItem
              key={normalizedOption.value || normalizedOption.label || index}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (typeof activeMenuItem?.onMenuSelect === "function") {
                  activeMenuItem.onMenuSelect(
                    normalizedOption.value ?? normalizedOption.label ?? normalizedOption,
                    normalizedOption
                  );
                }
                closeItemMenu();
              }}
              sx={{ fontSize: 13 }}
            >
              {normalizedOption.label}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Arrows */}
      {showArrows && hasItems && scrollMeta.hasOverflow && (
        <>
          {/* LEFT ARROW */}
          {showLeftArrow && (
            <IconButton
              onClick={() => scrollByStep(-1)}
              size="small"
              sx={{
                height: "40px",
                width: "24px",
                p: 0,
                position: "absolute",
                left: 4,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "transparent",
                border: 0,
                borderRadius: 0,
                boxShadow: "none",
                "&:hover": { backgroundColor: "transparent" },
              }}
              aria-label="Previous stats"
            >
              <Box
                component={rightArrowIcon}
                sx={{
                  color: PURPLE,
                  width: "24px",
                  height: "24px",
                  display: "block",
                  transform: "rotate(180deg)",
                }}
              />
            </IconButton>
          )}

          {/* RIGHT ARROW (hides at end when loop=false) */}
          {showRightArrow && (
            <IconButton
              onClick={() => scrollByStep(1)}
              size="small"
              sx={{
                height: "40px",
                width: "24px",
                p: 0,
                position: "absolute",
                right: 32,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "transparent !important",
                border: 0,
                borderRadius: 0,
                boxShadow: "none",
              }}
              aria-label="Next stats"
            >
              <Box
                component={rightArrowIcon}
                sx={{
                  color: PURPLE,
                  width: "24px",
                  height: "24px",
                  display: "block",
                }}
              />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
}
